"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import {
  Container,
  Text,
  Button,
  Stack,
  Group,
  Loader,
  Center,
  Alert,
  Paper,
  Textarea,
  ActionIcon,
  Badge,
  Image as MantineImage,
  Box,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconSend, IconPhoto, IconX, IconArrowLeft, IconSparkles } from "@tabler/icons-react";
import { doc, collection, getFirestore, onSnapshot, orderBy, query } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadString, getDownloadURL } from "firebase/storage";
import firebase_app from "@/firebase/config";
import { PRICING } from "@/constants/pricing";

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  imageUrl?: string;
  createdAt: string;
};

const STARTERS = [
  "Show me a date-night outfit in my colors",
  "What should I wear to a wedding this month?",
  "Rate my outfit (I'll send a photo)",
  "Build me a work capsule from my palette",
  "What coat should I buy this season?",
];

function resizeToDataUrl(file: File, maxDim: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const scale = Math.min(maxDim / img.width, maxDim / img.height, 1);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d")?.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(img.src);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error("This photo could not be opened. Try a JPEG or PNG image.")); };
    img.src = URL.createObjectURL(file);
  });
}

function StylistInner() {
  const searchParams = useSearchParams();
  const memberId =
    searchParams.get("id") || (typeof window !== "undefined" ? localStorage.getItem("styleClubMemberId") : null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [pendingImage, setPendingImage] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!memberId) return;
    const firestore = getFirestore(firebase_app);
    const unsub = onSnapshot(
      query(collection(firestore, "styleClubMembers", memberId, "chat"), orderBy("createdAt", "asc")),
      (snap) => { setMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ChatMessage, "id">) }))); setHistoryLoaded(true); },
      () => { setHistoryLoaded(true); setError("Your conversation could not load. Refresh after checking your connection."); }
    );
    const unsubMember = onSnapshot(doc(firestore, "styleClubMembers", memberId), (snap) => {
      if (!snap.exists()) { setError("Your membership could not be found. Return to your member page to recover access."); return; }
      const member = snap.data();
      setMembershipStatus(member.subscriptionStatus || member.status || null);
      const currentMonth = new Date().toISOString().slice(0, 7);
      const used = member.chatMonth === currentMonth ? Number(member.chatCount || 0) : 0;
      setRemaining(Math.max(0, PRICING.STYLE_CLUB.CHAT_MESSAGES_PER_MONTH - used));
    }, () => setError("Your membership could not load. Return to your member page to recover access."));
    return () => { unsub(); unsubMember(); };
  }, [memberId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, sending]);

  const send = async (text?: string) => {
    const message = (text ?? input).trim();
    if (!message || sending || !memberId || !historyLoaded || remaining === 0) return;
    setSending(true);
    setError(null);
    const attachedImage = pendingImage;

    try {
      let imageUrl: string | undefined;
      if (attachedImage) {
        const storage = getStorage(firebase_app);
        const path = `styleClubMembers/${memberId}/chat/${Date.now()}.jpg`;
        const imgRef = storageRef(storage, path);
        await uploadString(imgRef, attachedImage, "data_url");
        imageUrl = await getDownloadURL(imgRef);

      }

      const res = await fetch("/api/style-club/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ memberId, message, imageUrl }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      if (typeof data.remaining === "number") setRemaining(data.remaining);
      setInput("");
      setPendingImage(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong — try again.");
    } finally {
      setSending(false);
    }
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    try { setPendingImage(await resizeToDataUrl(file, 1000)); }
    catch (failure) { setError(failure instanceof Error ? failure.message : "That photo could not be attached."); }
  };

  if (!memberId) {
    return (
      <Container size="xl" py={48}>
        <Alert icon={<IconAlertCircle size={18} />} color="red" radius="md">
          We couldn&apos;t find your membership. <Link href="/style-club/access">Email yourself a private access link</Link>, or contact hi@coloranalysistech.com.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="lg">
      <Stack gap="md" style={{ minHeight: "calc(100vh - 200px)" }}>
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <Button
              component={Link}
              href={`/style-club/member?id=${memberId}`}
              variant="subtle"
              color="gray"
              size="xs"
              leftSection={<IconArrowLeft size={14} />}
            >
              My drops
            </Button>
          </Group>
          <Group gap="xs">
            <Badge variant="light" color="violet" leftSection={<IconSparkles size={12} />}>
              Your stylist
            </Badge>
            {remaining !== null && (
              <Tooltip label={`${PRICING.STYLE_CLUB.CHAT_MESSAGES_PER_MONTH} messages/month, resets on the 1st`}>
                <Badge variant="light" color="gray">
                  {remaining} left
                </Badge>
              </Tooltip>
            )}
          </Group>
        </Group>

        {/* Thread */}
        <Stack gap="sm" style={{ flex: 1 }}>
          {!historyLoaded && <Group><Loader size="sm" /><Text size="sm">Loading your conversation…</Text></Group>}
          {historyLoaded && messages.length === 0 && (
            <Paper radius="md" p="lg" bg="violet.0">
              <Text size="sm" fw={500} mb={4}>
                Hi — I&apos;m your stylist. 👋
              </Text>
              <Text size="sm" c="dimmed" mb="sm">
                I know your season, your body type, and your lifestyle. Ask me what to wear to anything, send me an
                outfit photo for feedback, or let&apos;s build you a capsule.
              </Text>
              <Group gap={6}>
                {STARTERS.map((s) => (
                  <Button key={s} variant="light" color="violet" size="compact-xs" radius="xl" disabled={sending || !historyLoaded || remaining === 0} onClick={() => send(s)}>
                    {s}
                  </Button>
                ))}
              </Group>
            </Paper>
          )}

          {messages.map((m) => (
            <Group key={m.id} justify={m.role === "user" ? "flex-end" : "flex-start"} align="flex-start">
              <Paper
                radius="lg"
                p="sm"
                maw={720}
                bg={m.role === "user" ? "violet.6" : "gray.1"}
                c={m.role === "user" ? "white" : "dark.8"}
              >
                {m.imageUrl && (
                  <MantineImage src={m.imageUrl} alt="Outfit photo" radius="md" maw={220} mb={6} />
                )}
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                  {m.content}
                </Text>
              </Paper>
            </Group>
          ))}

          {sending && (
            <Group justify="flex-start">
              <Paper radius="lg" p="sm" bg="gray.1">
                <Loader size="xs" color="violet" type="dots" />
              </Paper>
            </Group>
          )}
          <div ref={bottomRef} />
        </Stack>

        {error && (
          <Alert color="red" radius="md" p="xs">
            <Text size="xs">{error}</Text>
          </Alert>
        )}

        <Text size="xs" c="dimmed">{PRICING.STYLE_CLUB.CHAT_MESSAGES_PER_MONTH} AI stylist messages per calendar month. Resets on the 1st. {remaining === 0 ? "Your monthly allowance is used; your saved conversation stays available." : "Ask for practical options, use your closet, or share an outfit photo."}</Text>
        {membershipStatus && !["active", "trialing"].includes(membershipStatus) && <Alert color="orange">Your membership may need billing attention. Check your member page if new messages are unavailable.</Alert>}

        {/* Composer */}
        <Paper withBorder radius="lg" p="xs">
          {pendingImage && (
            <Group gap="xs" mb={6}>
              <Box style={{ position: "relative" }}>
                <MantineImage src={pendingImage} alt="Attached outfit" radius="sm" h={64} w={48} fit="cover" />
                <ActionIcon
                  aria-label="Remove attached photo"
                  size="xs"
                  color="dark"
                  variant="filled"
                  radius="xl"
                  style={{ position: "absolute", top: -6, right: -6 }}
                  onClick={() => setPendingImage(null)}
                >
                  <IconX size={10} />
                </ActionIcon>
              </Box>
              <Text size="xs" c="dimmed">
                Photo attached — ask me anything about this outfit
              </Text>
            </Group>
          )}
          <Group gap="xs" align="flex-end">
            <input
              aria-label="Attach an outfit photo"
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => { void handleFile(e.target.files?.[0] || null); e.target.value = ""; }}
            />
            <ActionIcon aria-label="Attach an outfit photo" disabled={sending} variant="subtle" color="gray" size="lg" onClick={() => fileRef.current?.click()}>
              <IconPhoto size={18} />
            </ActionIcon>
            <Textarea
              aria-label="Message your stylist"
              disabled={sending}
              placeholder="Ask your stylist anything…"
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              autosize
              minRows={1}
              maxRows={4}
              style={{ flex: 1 }}
              variant="unstyled"
              px="xs"
            />
            <ActionIcon aria-label="Send message" color="violet" size="lg" radius="xl" onClick={() => send()} loading={sending} disabled={!input.trim() || !historyLoaded || remaining === 0}>
              <IconSend size={16} />
            </ActionIcon>
          </Group>
        </Paper>
      </Stack>
    </Container>
  );
}

export default function StylistChatPage() {
  return (
    <Suspense
      fallback={
        <Center py={120}>
          <Loader color="violet" />
        </Center>
      }
    >
      <StylistInner />
    </Suspense>
  );
}
